// Reusable care-setting profiles sit between the canonical role library and
// patient experiences such as Hospital Journeys and future QR/deep-link landings.
// Keep role identities canonical: every entry below is a roleKey from roleLibrary.json.

export const CARE_SETTING_PROFILES = Object.freeze({
  daySurgery: {
    id: 'day-surgery',
    label: 'Day Surgery',
    locationGroup: 'theatres',
    coreRoleKeys: [
      'pre_assessment_nurse',
      'surgeon',
      'anaesthetist',
      'operating_department_practitioner',
      'theatre_nurse',
      'theatre_recovery_nurse',
      'healthcare_assistant',
    ],
    possibleRoleKeys: [
      'hospital_porter',
      'pharmacist',
      'physiotherapist',
    ],
  },
  maternity: {
    id: 'maternity',
    label: 'Maternity',
    locationGroup: 'maternity',
    coreRoleKeys: [
      'midwife',
      'maternity_support_worker',
      'sonographer',
      'obstetrician',
      'anaesthetist',
      'health_visitor',
    ],
    possibleRoleKeys: [
      'consultant_midwife',
      'operating_department_practitioner',
      'theatre_nurse',
      'theatre_recovery_nurse',
      'neonatal_nurse',
    ],
  },
  emergencyDepartment: {
    id: 'emergency-department',
    label: 'Emergency Department',
    locationGroup: 'emergency',
    coreRoleKeys: [
      'resident_doctor_previously_referred_to_as_junior_doctor',
      'staff_nurse',
      'healthcare_assistant',
    ],
    possibleRoleKeys: [
      'advanced_care_practitioner',
      'physician_associate',
      'paramedic',
      'radiographer',
      'pharmacist',
    ],
  },
  outpatients: {
    id: 'outpatients',
    label: 'Outpatients',
    locationGroup: 'outpatients',
    coreRoleKeys: [
      'consultant',
      'staff_nurse',
      'healthcare_assistant',
    ],
    possibleRoleKeys: [
      'pre_assessment_nurse',
      'physician_associate',
      'advanced_care_practitioner',
      'pharmacist',
      'physiotherapist',
    ],
  },
  imagingDiagnostics: {
    id: 'imaging-diagnostics',
    label: 'Imaging & Diagnostics',
    locationGroup: 'diagnostics',
    coreRoleKeys: [
      'radiographer',
      'sonographer',
      'radiologist',
    ],
    possibleRoleKeys: [
      'healthcare_assistant',
      'staff_nurse',
    ],
  },
});

// Studio and other configuration surfaces should consume this canonical list
// rather than maintaining a second allowlist that can drift from the profiles.
export const CARE_SETTING_PROFILE_KEYS = Object.freeze(Object.keys(CARE_SETTING_PROFILES));

export const TRUST_CARE_SETTING_OVERRIDES = Object.freeze({
  // Trust overlays are intentionally sparse. They inherit the base profile and
  // only describe genuine local differences.
  kch: {
    daySurgery: {
      possibleRoleKeys: ['hospital_porter'],
    },
  },
});

const unique = (values = []) => [...new Set(values.filter(Boolean))];

export function applyCareSettingOverride(profile, override = null) {
  if (!profile) return null;
  if (!override) {
    return {
      ...profile,
      coreRoleKeys: [...profile.coreRoleKeys],
      possibleRoleKeys: [...profile.possibleRoleKeys],
    };
  }

  const roleAssignments = override.roleAssignments && typeof override.roleAssignments === 'object'
    ? override.roleAssignments
    : null;

  let coreRoleKeys = unique(override.coreRoleKeys ?? profile.coreRoleKeys);
  let possibleRoleKeys = unique(override.possibleRoleKeys ?? profile.possibleRoleKeys)
    .filter((roleKey) => !coreRoleKeys.includes(roleKey));

  if (roleAssignments) {
    const assignmentByRole = new Map();
    coreRoleKeys.forEach((roleKey) => assignmentByRole.set(roleKey, 'core'));
    possibleRoleKeys.forEach((roleKey) => assignmentByRole.set(roleKey, 'possible'));

    Object.entries(roleAssignments).forEach(([roleKey, assignment]) => {
      if (assignment === 'excluded' || assignment == null) {
        assignmentByRole.delete(roleKey);
      } else if (assignment === 'core' || assignment === 'possible') {
        assignmentByRole.set(roleKey, assignment);
      }
    });

    coreRoleKeys = [...assignmentByRole.entries()]
      .filter(([, assignment]) => assignment === 'core')
      .map(([roleKey]) => roleKey);
    possibleRoleKeys = [...assignmentByRole.entries()]
      .filter(([, assignment]) => assignment === 'possible')
      .map(([roleKey]) => roleKey);
  }

  return {
    ...profile,
    ...override,
    roleAssignments: roleAssignments ? { ...roleAssignments } : undefined,
    coreRoleKeys,
    possibleRoleKeys,
  };
}

export function getCareSettingProfile(profileKey, { trustId = null, overlay = null } = {}) {
  const base = CARE_SETTING_PROFILES[profileKey];
  if (!base) return null;

  const trustOverride = trustId ? TRUST_CARE_SETTING_OVERRIDES[trustId]?.[profileKey] : null;
  const trustResolved = applyCareSettingOverride(base, trustOverride);
  return applyCareSettingOverride(trustResolved, overlay);
}

export function getCareSettingRoleKeys(profileKey, options = {}) {
  const profile = getCareSettingProfile(profileKey, options);
  if (!profile) return [];
  return unique([...profile.coreRoleKeys, ...profile.possibleRoleKeys]);
}
